import { useState } from "react";
import { Link } from "react-router-dom";
import { JOB_STATUSES, normalizeStatus } from "../lib/jobStatus";
import { PayModeBadge } from "../lib/payMode";
import { fmtKc } from "../lib/format";

const COLUMNS = JOB_STATUSES.map((s) => ({ key: s.key, label: s.label, color: s.colBorder, bg: s.colBg }));

const fmt = fmtKc;

export default function JobsKanban({ jobs, onMove }) {
    const [overCol, setOverCol] = useState(null);
    const byCol = COLUMNS.reduce((acc, c) => { acc[c.key] = []; return acc; }, {});
    jobs.forEach((j) => { byCol[normalizeStatus(j.status)].push(j); });

    const onDragStart = (e, jobId) => {
        e.dataTransfer.setData("text/plain", jobId);
        e.dataTransfer.effectAllowed = "move";
    };
    const onDragOver = (e, col) => { e.preventDefault(); setOverCol(col); };
    const onDrop = (e, col) => {
        e.preventDefault();
        const jobId = e.dataTransfer.getData("text/plain");
        setOverCol(null);
        if (jobId) onMove(jobId, col);
    };

    return (
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3" data-testid="jobs-kanban">
            {COLUMNS.map((col) => {
                const items = byCol[col.key] || [];
                const totalCosts = items.reduce((s, j) => s + (j.costs || []).reduce((ss, c) => ss + (c.total || c.quantity * c.unit_price || 0), 0), 0);
                return (
                    <div key={col.key}
                         onDragOver={(e) => onDragOver(e, col.key)}
                         onDragLeave={() => setOverCol(null)}
                         onDrop={(e) => onDrop(e, col.key)}
                         data-testid={`kanban-col-${col.key}`}
                         className={`bg-white border-t-4 ${col.color} ${overCol === col.key ? "ring-2 ring-orange-500" : ""}`}>
                        <div className="px-4 py-3 border-b border-slate-200">
                            <div className="flex items-center justify-between">
                                <span className="font-display font-bold text-slate-900 uppercase tracking-wider text-xs">{col.label}</span>
                                <span className="text-xs tabular-data text-slate-500">{items.length}</span>
                            </div>
                            {totalCosts > 0 && <div className="text-[10px] text-slate-500 tabular-data mt-1">∑ {fmt(totalCosts)}</div>}
                        </div>
                        <div className={`${col.bg} min-h-[60vh] p-2 space-y-2`}>
                            {items.length === 0 && <div className="text-center text-slate-300 text-xs py-8">Žádná zakázka</div>}
                            {items.map((j) => {
                                const total = (j.costs || []).reduce((s, c) => s + (c.total || c.quantity * c.unit_price || 0), 0);
                                return (
                                    <div key={j.id} draggable onDragStart={(e) => onDragStart(e, j.id)}
                                         data-testid={`kanban-card-${j.id}`}
                                         className="bg-white border border-slate-200 p-3 cursor-grab active:cursor-grabbing hover:border-orange-400 hover:shadow-sm transition-all">
                                        <Link to={`/zakazky/${j.id}`} className="font-medium text-slate-900 text-sm hover:text-orange-600 block leading-tight mb-1.5">
                                            {j.title}
                                        </Link>
                                        {j.client_name && <div className="text-xs text-slate-500 truncate">{j.client_name}</div>}
                                        {j.site_address && <div className="text-[10px] text-slate-400 truncate">{j.site_address}</div>}
                                        <div className="flex items-center justify-between mt-2 pt-2 border-t border-slate-100">
                                            <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-slate-400">
                                                <PayModeBadge mode={j.pay_mode} testid={`kanban-pay-mode-${j.id}`} />{j.is_test && <span className="inline-block px-1 py-0.5 text-[9px] font-bold uppercase tracking-wider border border-fuchsia-400 bg-fuchsia-50 text-fuchsia-800">Test</span>}{j.start_date || ""}
                                            </span>
                                            {total > 0 && <span className="text-xs font-semibold tabular-data text-slate-700">{fmt(total)}</span>}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                );
            })}
        </div>
    );
}
